import { useEffect, useState } from "react";
import { adminHttp } from "@/lib/api";
import { toast } from "sonner";
import {
  Save,
  ExternalLink,
  Loader2,
  Eye,
  EyeOff,
  Copy,
  RefreshCcw,
  KeyRound,
} from "lucide-react";

const PROPERTY_SAMPLE = `{
  "items": [
    {
      "slug": "palm-shell-villa",
      "external_id": "sheet-row-42",
      "title": "Shell Villa on Palm Jumeirah",
      "location": "Palm Jumeirah, Dubai",
      "area": "Frond N",
      "price_aed": 74000000,
      "price_usd": 20140000,
      "bedrooms": 6,
      "bathrooms": 8,
      "size_sqft": 11500,
      "type": "Villa",
      "status": "For Sale",
      "description": "Beachfront shell villa with private pontoon.",
      "features": ["Private Beach", "Infinity Pool", "Home Cinema"],
      "images": [
        "https://res.cloudinary.com/YOUR/image/upload/v1/shell-01.jpg",
        "https://res.cloudinary.com/YOUR/image/upload/v1/shell-02.jpg"
      ],
      "featured": true
    },
    {
      "slug": "old-listing-slug",
      "deleted": true
    }
  ]
}`;

const BLOG_SAMPLE = `{
  "items": [
    {
      "slug": "dubai-hills-2026",
      "external_id": "sheet-row-08",
      "title": "Dubai Hills — 2026 Outlook",
      "excerpt": "Family estates and paddle courts drive the next wave.",
      "body": "Full markdown / long-form body copy goes here...",
      "category": "Market Insights",
      "cover_image": "https://res.cloudinary.com/YOUR/image/upload/v1/hills.jpg",
      "read_minutes": 5,
      "published_at": "2026-02-18"
    }
  ]
}`;

function CopyButton({ text, label = "Copy" }) {
  const [ok, setOk] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setOk(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setOk(false), 1500);
    } catch {
      toast.error("Copy failed.");
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-2 px-3 py-2 border border-[#c5a059]/40 text-[#c5a059] uppercase text-[9px] tracking-[0.3em] hover:bg-[#c5a059] hover:text-black transition-colors"
    >
      <Copy size={12} /> {ok ? "Copied" : label}
    </button>
  );
}

export default function SettingsPanel() {
  const [webhook, setWebhook] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [apiKey, setApiKey] = useState("");
  const [keyUpdatedAt, setKeyUpdatedAt] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [rotating, setRotating] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const propertiesEndpoint = `${backendUrl}/api/integrations/properties/upsert`;
  const blogsEndpoint = `${backendUrl}/api/integrations/blogs/upsert`;

  useEffect(() => {
    Promise.all([
      adminHttp.get("/admin/settings"),
      adminHttp.get("/admin/integration-key"),
    ])
      .then(([s, k]) => {
        setWebhook(s.data.n8n_webhook_url || "");
        setApiKey(k.data.api_key || "");
        setKeyUpdatedAt(k.data.updated_at || null);
      })
      .catch(() => toast.error("Failed to load settings."))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminHttp.put("/admin/settings", { n8n_webhook_url: webhook });
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    if (!webhook.trim()) {
      toast.error("Save a URL first.");
      return;
    }
    try {
      const { data } = await adminHttp.post("/admin/settings/test-webhook");
      if (data.ok) toast.success(`Test ping accepted (${data.status}).`);
      else toast.error(`n8n returned ${data.status}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reach the webhook.");
    }
  };

  const rotateKey = async () => {
    if (
      !window.confirm(
        "Rotate the Integration API key? Any n8n workflow using the current key will stop working until updated."
      )
    )
      return;
    setRotating(true);
    try {
      const { data } = await adminHttp.post("/admin/integration-key/rotate");
      setApiKey(data.api_key);
      setKeyUpdatedAt(data.updated_at);
      setShowKey(true);
      toast.success("Key rotated. Update your n8n credentials.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rotate failed.");
    } finally {
      setRotating(false);
    }
  };

  const maskedKey = apiKey ? `${apiKey.slice(0, 6)}${"•".repeat(24)}${apiKey.slice(-4)}` : "";

  return (
    <div data-testid="admin-settings-panel">
      <div className="mb-10">
        <div className="underline-label mb-2">Integrations</div>
        <h2 className="font-display text-3xl text-[#1c1c1c]">Settings</h2>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#6a6259] text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-14">
          {/* n8n webhook */}
          <form onSubmit={save} className="border-t border-[#1c1c1c]/10 pt-8">
            <div className="underline-label mb-3">n8n → Google Sheets Webhook</div>
            <p className="text-[#6a6259] text-sm leading-relaxed mb-6 max-w-2xl">
              Paste the production webhook URL from your n8n workflow. Every contact-form submission
              will be POSTed here, then routed by n8n into your Google Sheets database.
            </p>

            <input
              data-testid="settings-n8n-webhook"
              type="url"
              className="editorial-input"
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              placeholder="https://your-n8n-host.app/webhook/azefac-leads"
            />

            <div className="mt-6 flex items-center gap-3">
              <button
                type="submit"
                data-testid="settings-save"
                disabled={busy}
                className="inline-flex items-center gap-3 px-7 py-3 bg-[#c5a059] text-black uppercase text-[10px] tracking-[0.32em] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] transition-colors disabled:opacity-60"
              >
                <Save size={13} />
                {busy ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                data-testid="settings-test"
                onClick={test}
                className="inline-flex items-center gap-3 px-5 py-3 border border-[#c5a059]/50 text-[#c5a059] uppercase text-[10px] tracking-[0.32em] hover:bg-[#c5a059] hover:text-black transition-colors"
              >
                <ExternalLink size={13} /> Send Test Ping
              </button>
            </div>
          </form>

          {/* Integration API Key */}
          <section className="border-t border-[#1c1c1c]/10 pt-8" data-testid="integration-api-section">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={14} className="text-[#c5a059]" />
              <div className="underline-label">External Integration API</div>
            </div>
            <p className="text-[#6a6259] text-sm leading-relaxed mb-6 max-w-2xl">
              Use this key from your n8n workflow to <em>bulk create, update or delete</em> Properties
              and Journal entries directly from Google Sheets. Send it in the{" "}
              <code className="text-[#c5a059]">X-API-Key</code> HTTP header.
            </p>

            <div className="bg-[#1c1c1c]/[0.03] border border-[#1c1c1c]/10 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="uppercase text-[10px] tracking-[0.32em] text-[#6a6259]">API Key</div>
                {keyUpdatedAt && (
                  <div className="text-[10px] text-[#6a6259]">
                    Rotated · {new Date(keyUpdatedAt).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <code
                  data-testid="integration-api-key-value"
                  className="flex-1 min-w-[240px] px-3 py-2 bg-white border border-[#1c1c1c]/10 text-[#1c1c1c] text-sm font-mono break-all"
                >
                  {showKey ? apiKey : maskedKey}
                </code>
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  data-testid="integration-toggle-visibility"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-[#c5a059]/40 text-[#c5a059] uppercase text-[9px] tracking-[0.3em] hover:bg-[#c5a059] hover:text-black transition-colors"
                >
                  {showKey ? <EyeOff size={12} /> : <Eye size={12} />}
                  {showKey ? "Hide" : "Reveal"}
                </button>
                <CopyButton text={apiKey} label="Copy key" />
                <button
                  type="button"
                  onClick={rotateKey}
                  disabled={rotating}
                  data-testid="integration-rotate-key"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-[#c5a059]/50 text-[#c5a059] uppercase text-[9px] tracking-[0.3em] hover:bg-[#c5a059] hover:text-black transition-colors disabled:opacity-60"
                >
                  <RefreshCcw size={12} className={rotating ? "animate-spin" : ""} />
                  {rotating ? "Rotating…" : "Rotate"}
                </button>
              </div>
            </div>

            {/* Endpoints */}
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div className="border border-[#1c1c1c]/10 p-5">
                <div className="uppercase text-[10px] tracking-[0.32em] text-[#6a6259] mb-2">
                  Properties · Upsert
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="flex-1 min-w-[220px] px-3 py-2 bg-white border border-[#1c1c1c]/10 text-[#1c1c1c] text-xs font-mono break-all">
                    POST {propertiesEndpoint}
                  </code>
                  <CopyButton text={propertiesEndpoint} label="Copy URL" />
                </div>
              </div>
              <div className="border border-[#1c1c1c]/10 p-5">
                <div className="uppercase text-[10px] tracking-[0.32em] text-[#6a6259] mb-2">
                  Journals · Upsert
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="flex-1 min-w-[220px] px-3 py-2 bg-white border border-[#1c1c1c]/10 text-[#1c1c1c] text-xs font-mono break-all">
                    POST {blogsEndpoint}
                  </code>
                  <CopyButton text={blogsEndpoint} label="Copy URL" />
                </div>
              </div>
            </div>

            {/* Reference */}
            <div className="mt-8 space-y-2 text-[#6a6259] text-sm leading-relaxed max-w-3xl">
              <p>
                <strong className="text-[#1c1c1c]">Behaviour:</strong> each item is matched by{" "}
                <code className="text-[#c5a059]">slug</code>. Existing rows are updated with the fields
                you send; new rows require all core fields. Pass{" "}
                <code className="text-[#c5a059]">{'"deleted": true'}</code> to remove a listing.
              </p>
              <p>
                <strong className="text-[#1c1c1c]">Limits:</strong> up to 100 items per request.
                Invalid rows are reported in the response{" "}
                <code className="text-[#c5a059]">errors[]</code> array — valid rows still commit.
              </p>
              <p>
                <strong className="text-[#1c1c1c]">Images:</strong> pass full public URLs
                (Cloudinary works out of the box).
              </p>
            </div>

            {/* Payload samples */}
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="uppercase text-[10px] tracking-[0.32em] text-[#6a6259]">
                    Sample · Properties payload
                  </div>
                  <CopyButton text={PROPERTY_SAMPLE} label="Copy JSON" />
                </div>
                <pre
                  data-testid="integration-sample-properties"
                  className="bg-[#1c1c1c] text-[#e8dcc0] text-[11px] leading-relaxed p-4 overflow-x-auto max-h-[380px]"
                >
                  <code>{PROPERTY_SAMPLE}</code>
                </pre>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="uppercase text-[10px] tracking-[0.32em] text-[#6a6259]">
                    Sample · Journals payload
                  </div>
                  <CopyButton text={BLOG_SAMPLE} label="Copy JSON" />
                </div>
                <pre
                  data-testid="integration-sample-blogs"
                  className="bg-[#1c1c1c] text-[#e8dcc0] text-[11px] leading-relaxed p-4 overflow-x-auto max-h-[380px]"
                >
                  <code>{BLOG_SAMPLE}</code>
                </pre>
              </div>
            </div>

            {/* n8n hint */}
            <div className="mt-8 p-5 border-l-2 border-[#c5a059] bg-[#c5a059]/5">
              <div className="uppercase text-[10px] tracking-[0.32em] text-[#c5a059] mb-2">
                n8n HTTP Request node
              </div>
              <ul className="text-[#1c1c1c] text-sm leading-relaxed space-y-1 list-disc pl-5">
                <li>Method: <code className="text-[#c5a059]">POST</code></li>
                <li>URL: one of the endpoints above</li>
                <li>
                  Headers:{" "}
                  <code className="text-[#c5a059]">X-API-Key: {"{{ your integration key }}"}</code>{" "}
                  and{" "}
                  <code className="text-[#c5a059]">Content-Type: application/json</code>
                </li>
                <li>
                  Body (JSON): wrap your rows in{" "}
                  <code className="text-[#c5a059]">{'{ "items": [ ... ] }'}</code>
                </li>
              </ul>
            </div>
          </section>

          {/* Account */}
          <div className="border-t border-[#1c1c1c]/10 pt-8">
            <div className="underline-label mb-3">Account</div>
            <div className="text-[#6a6259] text-sm">
              Admin credentials are stored in <code className="text-[#c5a059]">/app/backend/.env</code>{" "}
              as <code className="text-[#c5a059]">ADMIN_EMAIL</code> and{" "}
              <code className="text-[#c5a059]">ADMIN_PASSWORD</code>. Restart the backend after
              changing them.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
